import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Calendar as CalIcon, Plus, Video, MapPin, Users, Trash2, Repeat,
  AlertTriangle, ShieldCheck, Clock3, Tv, PenSquare, MonitorSmartphone, Wifi, Phone, Presentation, Check, X, Search, ChevronDown,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";

const FEATURE_ICONS = {
  tv: Tv, whiteboard: PenSquare, video_conference: MonitorSmartphone,
  projector: Presentation, phone: Phone, wifi: Wifi,
};
const FEATURE_LABELS = {
  tv: "TV", whiteboard: "Whiteboard", video_conference: "Video conf",
  projector: "Projector", phone: "Phone", wifi: "Wi-Fi",
};

export default function Meetings() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "hr"].includes(user?.role);
  const [tab, setTab] = useState("mine");
  const [list, setList] = useState([]);
  const [pending, setPending] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();

  // Deep-link from RoomAvailability: ?new=1&room_id=...&start=ISO&end=ISO
  useEffect(() => {
    if (params.get("new") === "1") {
      setPrefill({
        room_id: params.get("room_id") || "",
        starts_at: params.get("start") || "",
        ends_at: params.get("end") || "",
      });
      setOpen(true);
      // Strip the params so re-opening from scratch works cleanly
      const next = new URLSearchParams();
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const load = async () => {
    const [m, e, r] = await Promise.all([
      api.get("/meetings", { params: { scope: "mine" } }),
      api.get("/employees"),
      api.get("/rooms"),
    ]);
    setList(m.data);
    setEmployees(e.data);
    setRooms(r.data);
    if (canApprove) {
      try {
        const p = await api.get("/meetings/pending-approval");
        setPending(p.data);
      } catch { /* noop */ }
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Cancel this meeting?")) return;
    try { await api.delete(`/meetings/${id}`); toast.success("Meeting cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upcoming = list.filter((m) => m.status === "scheduled"
    && m.approval_status !== "rejected"
    && new Date(m.ends_at) >= new Date());
  const past = list.filter((m) => m.status !== "scheduled"
    || m.approval_status === "rejected"
    || new Date(m.ends_at) < new Date());

  return (
    <div className="p-6 space-y-5 animate-fade-up" data-testid="meetings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Meetings</h1>
          <p className="text-sm text-slate-500 mt-1">Book a room, invite the team, HR handles the exceptions.</p>
        </div>
        <div className="flex items-center gap-2">
          {canApprove && (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="mine" data-testid="tab-mine">My meetings</TabsTrigger>
                <TabsTrigger value="approvals" data-testid="tab-approvals">
                  Pending approval {pending.length > 0 && <Badge className="ml-1.5 bg-amber-500 text-white rounded-full h-5 min-w-5 px-1.5">{pending.length}</Badge>}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg" data-testid="new-meeting-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Schedule meeting
              </Button>
            </DialogTrigger>
            <NewMeetingDialog
              key={prefill ? `${prefill.room_id}-${prefill.starts_at}` : "fresh"}
              employees={employees.filter((e) => e.user_id !== user?.id)}
              rooms={rooms}
              prefill={prefill}
              onCreated={() => { setOpen(false); setPrefill(null); load(); }}
            />
          </Dialog>
        </div>
      </div>

      {tab === "approvals" && canApprove ? (
        <ApprovalsQueue items={pending} onDecide={load} />
      ) : (
        <>
          <section>
            <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Upcoming</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcoming.length === 0
                ? <div className="surface p-8 text-center text-slate-400 text-sm md:col-span-3">No upcoming meetings.</div>
                : upcoming.map((m) => (
                    <MeetingCard
                      key={m.id}
                      m={m}
                      canCancel={m.created_by === user?.id || canApprove}
                      onCancel={() => remove(m.id)}
                      onEdit={() => setEditing(m)}
                      employees={employees}
                    />
                  ))}
            </div>
          </section>

          <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
            {editing && (
              <NewMeetingDialog
                key={`edit-${editing.id}`}
                employees={employees.filter((e) => e.user_id !== editing.created_by)}
                rooms={rooms}
                initial={editing}
                onCreated={() => { setEditing(null); load(); }}
              />
            )}
          </Dialog>

          {past.length > 0 && (
            <section>
              <h2 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Past</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {past.map((m) => <MeetingCard key={m.id} m={m} past employees={employees} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function statusBadge(m) {
  if (m.status === "cancelled") return { label: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  const s = m.approval_status;
  if (s === "pending") return { label: "Pending HR approval", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  if (s === "approved") return { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (s === "rejected") return { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  return null;
}

// Render meeting description with automatic URL detection (so Google Meet /
// Zoom / any http(s) link is a real clickable anchor) plus a "Show more"
// toggle when the text runs past a few lines. `whitespace-pre-wrap` keeps
// user-authored line breaks intact.
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;

function LinkifiedText({ text }) {
  const parts = String(text).split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 1) {
          // Strip a trailing punctuation char that isn't part of the URL
          const cleaned = part.replace(/[.,;:!?)\]]+$/, "");
          const trailing = part.slice(cleaned.length);
          return (
            <span key={i}>
              <a
                href={cleaned}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline underline-offset-2 break-all"
                onClick={(e) => e.stopPropagation()}
                data-testid="meeting-desc-link"
              >{cleaned}</a>
              {trailing}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function MeetingDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const needsClamp = text.length > 220 || text.split("\n").length > 3;
  return (
    <div className="mt-2">
      <p
        className={`text-sm text-slate-600 whitespace-pre-wrap break-words ${
          needsClamp && !expanded ? "line-clamp-3" : ""
        }`}
        data-testid="meeting-description-body"
      >
        <LinkifiedText text={text} />
      </p>
      {needsClamp && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          data-testid="meeting-desc-toggle"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function MeetingCard({ m, canCancel, onCancel, onEdit, past, employees }) {
  const [expanded, setExpanded] = useState(false);
  const { user: me } = useAuth();
  const start = new Date(m.starts_at);
  const end = new Date(m.ends_at);
  const attendees = employees.filter((e) => m.attendee_user_ids?.includes(e.user_id));
  const organizer = employees.find((e) => e.user_id === m.organizer_user_id);
  // Participants list = organizer first, then invitees (no duplicates)
  const participants = [organizer, ...attendees.filter((a) => a.user_id !== m.organizer_user_id)].filter(Boolean);
  const badge = statusBadge(m);
  return (
    <div className={`surface p-5 card-hover ${past ? "opacity-70" : ""}`} data-testid={`meeting-card-${m.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-blue-600">
            <CalIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
            {start.toLocaleDateString(undefined, { month: "short", day: "numeric", weekday: "short" })}
          </div>
          <div className="flex items-start justify-between gap-2 mt-2">
            <h3 className="font-display text-lg font-medium text-slate-900 leading-snug break-words">{m.title}</h3>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} → {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            {typeof m.duration_minutes === "number" && <> · {m.duration_minutes}m</>}
          </div>
          {m.created_by_name && (
            <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5" data-testid={`meeting-organizer-${m.id}`}>
              <span className="h-5 w-5 rounded-full bg-slate-100 text-slate-700 grid place-items-center text-[10px] font-semibold">
                {m.created_by_name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
              </span>
              Organised by <span className="font-medium text-slate-900">{m.created_by_name}{m.created_by === me?.id && " (You)"}</span>
            </div>
          )}
          {m.meet_link && m.status !== "cancelled" && (
            <a
              href={m.meet_link}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 transition-colors"
              data-testid={`meeting-join-meet-${m.id}`}
            >
              <Video className="h-3.5 w-3.5" /> Join Google Meet
            </a>
          )}
          {m.add_google_meet && !m.meet_link && m.google_sync_error && m.status !== "cancelled" && (
            <div className="mt-3 text-[11px] text-rose-600 flex items-start gap-1" data-testid={`meeting-google-error-${m.id}`}>
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>Meet link not created: {m.google_sync_error} Edit &amp; save the meeting to retry.</span>
            </div>
          )}
          {m.description && <MeetingDescription text={m.description} />}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {badge && (
              <Badge variant="outline" className={`rounded-full font-medium text-[10px] ${badge.cls}`}>{badge.label}</Badge>
            )}
            {m.is_recurring && (
              <Badge variant="outline" className="rounded-full font-medium text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                <Repeat className="h-2.5 w-2.5 mr-1" /> Recurring
              </Badge>
            )}
          </div>
        </div>
        {canCancel && !past && m.status !== "cancelled" && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onEdit}
              className="text-slate-400 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-100"
              title="Edit meeting"
              data-testid={`edit-meeting-${m.id}`}
            >
              <PenSquare className="h-4 w-4" />
            </button>
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50"
              title="Cancel meeting"
              data-testid={`cancel-meeting-${m.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
          {m.room_name ? (
            <><MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{m.room_name}</span></>
          ) : m.location?.toLowerCase() === "online" ? (
            <><Video className="h-3.5 w-3.5" /> Online</>
          ) : (
            <><MapPin className="h-3.5 w-3.5" /> {m.location}</>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          disabled={participants.length === 0}
          className="flex items-center gap-1 group disabled:cursor-default disabled:opacity-60"
          data-testid={`meeting-toggle-participants-${m.id}`}
          aria-expanded={expanded}
          aria-controls={`meeting-participants-${m.id}`}
          title={participants.length > 0 ? (expanded ? "Hide participants" : "Show participants") : "No participants"}
        >
          <div className="flex items-center gap-1">
            {attendees.slice(0, 4).map((a) => (
              <Avatar key={a.id} className="h-6 w-6 ring-2 ring-white -ml-1.5">
                <AvatarImage src={a.avatar_url} />
                <AvatarFallback className="text-[10px] bg-slate-100">{a.name.split(" ").map(p => p[0]).slice(0, 2).join("")}</AvatarFallback>
              </Avatar>
            ))}
            {attendees.length > 4 && <span className="text-xs text-slate-500 ml-1">+{attendees.length - 4}</span>}
            {attendees.length === 0 && <span className="text-xs text-slate-400 flex items-center gap-1"><Users className="h-3 w-3" /> just you</span>}
          </div>
          {participants.length > 0 && (
            <ChevronDown
              className={`h-3.5 w-3.5 text-slate-400 transition-transform group-hover:text-slate-600 ${expanded ? "rotate-180" : ""}`}
              strokeWidth={1.5}
            />
          )}
        </button>
      </div>

      {expanded && participants.length > 0 && (
        <div
          id={`meeting-participants-${m.id}`}
          className="mt-3 border-t border-slate-100 pt-3 space-y-2 animate-fade-up"
          data-testid={`meeting-participants-${m.id}`}
        >
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 flex items-center gap-1.5">
            <Users className="h-3 w-3" /> Participants ({participants.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {participants.map((p) => {
              const isOrganizer = p.user_id === m.organizer_user_id;
              return (
                <div key={p.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <Avatar className="h-7 w-7 shrink-0">
                    <AvatarImage src={p.avatar_url} />
                    <AvatarFallback className="text-[10px] bg-slate-100">{p.name.split(" ").map(x => x[0]).slice(0, 2).join("")}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">{p.name}</div>
                    {p.designation && <div className="text-[11px] text-slate-500 truncate">{p.designation}</div>}
                  </div>
                  {isOrganizer && (
                    <Badge variant="outline" className="rounded-full font-medium text-[10px] bg-blue-50 text-blue-700 border-blue-200 shrink-0">
                      Organizer
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalsQueue({ items, onDecide }) {
  const [decision, setDecision] = useState(null); // { id, action, title }
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      await api.post(`/meetings/${decision.id}/${decision.action}`, { note });
      toast.success(decision.action === "approve" ? "Meeting approved & invites sent" : "Meeting rejected");
      setDecision(null); setNote(""); onDecide();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="surface p-0 overflow-hidden" data-testid="approvals-queue">
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <ShieldCheck className="h-8 w-8 mx-auto text-slate-300" strokeWidth={1.25} />
            <div className="mt-2 text-sm text-slate-500">No meetings awaiting approval.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left font-semibold px-5 py-3">Meeting</th>
                <th className="text-left font-semibold px-5 py-3">Requested by</th>
                <th className="text-left font-semibold px-5 py-3">Room</th>
                <th className="text-left font-semibold px-5 py-3">When</th>
                <th className="text-left font-semibold px-5 py-3">Reason</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((m) => (
                <tr key={m.id} data-testid={`pending-row-${m.id}`}>
                  <td className="px-5 py-3 text-slate-900 font-medium">{m.title}</td>
                  <td className="px-5 py-3 text-slate-600">{m.created_by_name}</td>
                  <td className="px-5 py-3 text-slate-600">{m.room_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">
                    <div>{new Date(m.starts_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(m.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} → {new Date(m.ends_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ({m.duration_minutes}m)
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {m.is_recurring
                      ? <Badge variant="outline" className="text-[10px] rounded-full bg-indigo-50 text-indigo-700 border-indigo-200"><Repeat className="h-2.5 w-2.5 mr-1" /> Recurring</Badge>
                      : <Badge variant="outline" className="text-[10px] rounded-full bg-amber-50 text-amber-700 border-amber-200"><Clock3 className="h-2.5 w-2.5 mr-1" /> {m.duration_minutes}m {'>'} 2h</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 mr-1.5"
                            onClick={() => { setDecision({ id: m.id, action: "approve", title: m.title }); setNote(""); }}
                            data-testid={`approve-meeting-${m.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50"
                            onClick={() => { setDecision({ id: m.id, action: "reject", title: m.title }); setNote(""); }}
                            data-testid={`reject-meeting-${m.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent className="rounded-2xl" data-testid="meeting-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {decision?.action === "approve" ? "Approve meeting" : "Reject meeting"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">"{decision?.title}"</div>
            <div>
              <Label>Note {decision?.action === "reject" ? "(shown to the requester)" : "(optional)"}</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1.5" data-testid="decision-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy}
                    className={decision?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
                    data-testid="decision-submit">
              {decision?.action === "approve" ? "Approve & notify" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function warnGoogle(m) {
  if (m?.add_google_meet && !m?.meet_link && m?.google_sync_error) {
    toast.error(`Google Meet link not created: ${m.google_sync_error}`, { duration: 10000 });
  }
}

function GoogleMeetToggle({ checked, onChange, existingLink }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/google/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  }, []);
  if (!status) return null;
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="google-meet-toggle-block">
      <label className={`flex items-center gap-2 text-sm ${status.connected ? "cursor-pointer text-slate-700" : "text-slate-400"}`}>
        <Checkbox
          checked={!!checked}
          disabled={!status.connected}
          onCheckedChange={(v) => onChange(!!v)}
          data-testid="meeting-add-google-meet"
        />
        <Video className="h-3.5 w-3.5 text-emerald-600" />
        Add Google Meet video link
      </label>
      <p className="text-[11px] text-slate-500 mt-1 ml-6">
        {status.connected
          ? "A Meet link is created and Google emails a calendar invite to every participant."
          : "Google Meet isn't connected yet. A Super Admin can connect it in Settings."}
      </p>
      {existingLink && (
        <p className="text-[11px] mt-1 ml-6 break-all">
          Current link: <a href={existingLink} target="_blank" rel="noreferrer" className="text-emerald-700 underline" data-testid="meeting-existing-meet-link">{existingLink}</a>
        </p>
      )}
    </div>
  );
}

function NewMeetingDialog({ employees, rooms, prefill, initial, onCreated }) {
  const isEdit = !!initial;
  const toLocal = (d) => {
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  };
  const initStart = initial?.starts_at
    ? new Date(initial.starts_at)
    : prefill?.starts_at
    ? new Date(prefill.starts_at)
    : (() => {
        const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); return d;
      })();
  const initEnd = initial?.ends_at
    ? new Date(initial.ends_at)
    : prefill?.ends_at
    ? new Date(prefill.ends_at)
    : (() => {
        const d = new Date(initStart); d.setMinutes(d.getMinutes() + 30); return d;
      })();

  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    starts_at: toLocal(initStart),
    ends_at: toLocal(initEnd),
    location: initial?.location || "Online",
    room_id: initial?.room_id || prefill?.room_id || "",
    is_recurring: initial?.is_recurring || false,
    recurrence: initial?.recurrence || { frequency: "weekly", count: 4 },
    attendee_user_ids: initial?.attendee_user_ids || [],
    add_google_meet: initial?.add_google_meet || false,
  });
  const [conflict, setConflict] = useState(null);
  const [checking, setChecking] = useState(false);
  const [inviteeQuery, setInviteeQuery] = useState("");

  const filteredEmployees = employees.filter((e) => {
    const q = inviteeQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.name || "").toLowerCase().includes(q) ||
      (e.designation || "").toLowerCase().includes(q) ||
      (e.department || "").toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q)
    );
  });
  const selectedInvitees = employees.filter((e) => form.attendee_user_ids.includes(e.user_id));

  // duration in minutes (frontend guard so the banner matches server)
  const durationMinutes = (() => {
    try {
      const ms = new Date(form.ends_at).getTime() - new Date(form.starts_at).getTime();
      return Math.max(0, Math.floor(ms / 60000));
    } catch { return 0; }
  })();
  const needsApproval = durationMinutes > 120 || form.is_recurring;
  const selectedRoom = rooms.find((r) => r.id === form.room_id);

  // Live conflict check whenever room + times change
  useEffect(() => {
    if (!form.room_id || !form.starts_at || !form.ends_at) { setConflict(null); return; }
    if (new Date(form.ends_at) <= new Date(form.starts_at)) { setConflict(null); return; }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/rooms/check-conflict", {
          room_id: form.room_id,
          starts_at: new Date(form.starts_at).toISOString(),
          ends_at: new Date(form.ends_at).toISOString(),
          // In edit mode the meeting being edited would otherwise flag ITSELF
          // as a conflict and lock the Save button.
          exclude_meeting_id: initial?.id,
        });
        if (!cancelled) setConflict(data.available ? null : data.conflict);
      } catch { /* noop */ }
      finally { if (!cancelled) setChecking(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.room_id, form.starts_at, form.ends_at, initial?.id]);

  const submit = async () => {
    if (!form.title) { toast.error("Add a title"); return; }
    if (new Date(form.ends_at) <= new Date(form.starts_at)) { toast.error("End must be after start"); return; }
    if (conflict) { toast.error("Room conflict — pick a different slot or room"); return; }
    if (selectedRoom && form.attendee_user_ids.length + 1 > selectedRoom.capacity) {
      toast.error(`${selectedRoom.name} seats ${selectedRoom.capacity} — you've invited ${form.attendee_user_ids.length + 1}`);
      return;
    }
    try {
      const payload = {
        title: form.title,
        description: form.description,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
        location: form.location || (selectedRoom ? selectedRoom.name : "Online"),
        room_id: form.room_id || null,
        attendee_user_ids: form.attendee_user_ids,
        is_recurring: form.is_recurring,
        recurrence: form.is_recurring ? form.recurrence : null,
        add_google_meet: form.add_google_meet,
      };
      if (isEdit) {
        const { data } = await api.patch(`/meetings/${initial.id}`, payload);
        toast.success("Meeting updated — participants notified");
        warnGoogle(data);
      } else {
        const { data } = await api.post("/meetings", payload);
        warnGoogle(data);
        if (data.approval_status === "pending") {
          toast.success("Meeting submitted — HR will review it shortly.");
        } else {
          toast.success("Meeting scheduled & invites sent");
        }
      }
      onCreated();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.message) toast.error(detail.message);
      else toast.error(formatApiError(detail));
    }
  };

  const toggle = (uid) => setForm({
    ...form,
    attendee_user_ids: form.attendee_user_ids.includes(uid)
      ? form.attendee_user_ids.filter((x) => x !== uid)
      : [...form.attendee_user_ids, uid],
  });

  return (
    <DialogContent className="rounded-2xl max-w-2xl" data-testid={isEdit ? "edit-meeting-dialog" : "new-meeting-dialog"}>
      <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit meeting" : "Schedule meeting"}</DialogTitle></DialogHeader>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <div>
          <Label>Title</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="meeting-title" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Starts</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1.5" data-testid="meeting-start" /></div>
          <div><Label>Ends</Label><Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1.5" data-testid="meeting-end" /></div>
        </div>

        <div>
          <Label>Meeting room</Label>
          <Select value={form.room_id || "__none__"} onValueChange={(v) => setForm({ ...form, room_id: v === "__none__" ? "" : v })}>
            <SelectTrigger className="mt-1.5" data-testid="meeting-room-select"><SelectValue placeholder="No room (online / anywhere)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                <div className="flex items-center gap-2 text-slate-500"><Video className="h-3.5 w-3.5" /> Online / no room</div>
              </SelectItem>
              {rooms.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-slate-500" />
                    <span>{r.name}</span>
                    <span className="text-xs text-slate-400">· {r.capacity} seats</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedRoom && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {selectedRoom.features.map((f) => {
                const Icon = FEATURE_ICONS[f];
                return (
                  <Badge key={f} variant="outline" className="text-[10px] rounded-full bg-slate-50 text-slate-600 border-slate-200">
                    {Icon && <Icon className="h-2.5 w-2.5 mr-1" strokeWidth={1.8} />}
                    {FEATURE_LABELS[f] || f}
                  </Badge>
                );
              })}
              {selectedRoom.location && <Badge variant="outline" className="text-[10px] rounded-full bg-slate-50 text-slate-600 border-slate-200">{selectedRoom.location}</Badge>}
            </div>
          )}
          {conflict && (
            <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 flex items-start gap-2" data-testid="room-conflict-banner">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <b>Room already booked</b> — {conflict.title} by {conflict.created_by_name} from
                {" "}{new Date(conflict.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} to {" "}
                {new Date(conflict.ends_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
                Pick a different slot or room.
              </div>
            </div>
          )}
          {!conflict && selectedRoom && !checking && form.room_id && (
            <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1" data-testid="room-available-hint">
              <ShieldCheck className="h-3.5 w-3.5" /> {selectedRoom.name} is available for this slot
            </div>
          )}
        </div>

        <GoogleMeetToggle
          checked={form.add_google_meet}
          onChange={(v) => setForm({ ...form, add_google_meet: v })}
          existingLink={initial?.meet_link}
        />

        <div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
            <Checkbox checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: !!v })} data-testid="meeting-recurring" />
            <Repeat className="h-3.5 w-3.5 text-slate-500" />
            Repeat this meeting
          </label>
          {form.is_recurring && (
            <div className="mt-2 grid grid-cols-2 gap-3">
              <Select value={form.recurrence.frequency} onValueChange={(v) => setForm({ ...form, recurrence: { ...form.recurrence, frequency: v } })}>
                <SelectTrigger data-testid="recurrence-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Every weekday</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={52}
                       value={form.recurrence.count}
                       onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, count: Number(e.target.value) } })}
                       data-testid="recurrence-count" />
                <span className="text-xs text-slate-500">occurrences</span>
              </div>
            </div>
          )}
        </div>

        {needsApproval && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="approval-banner">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <b>HR approval required</b> — {form.is_recurring ? "recurring meetings" : `bookings longer than 2 hours (${durationMinutes}m)`} need
              HR sign-off. Invites will be sent once HR approves.
            </div>
          </div>
        )}

        <div>
          <Label>Description (optional)</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 min-h-[70px]" data-testid="meeting-description" />
        </div>
        <div>
          <Label>Invite teammates</Label>

          {/* Selected chips — quick glance at who's already invited, click × to remove */}
          {selectedInvitees.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="invitee-chips">
              {selectedInvitees.map((e) => (
                <span
                  key={e.user_id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 pl-1 pr-2 py-1 text-xs"
                >
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={e.avatar_url} />
                    <AvatarFallback className="text-[9px]">{e.name.split(" ").map(p => p[0]).slice(0, 2).join("")}</AvatarFallback>
                  </Avatar>
                  {e.name}
                  <button
                    type="button"
                    onClick={() => toggle(e.user_id)}
                    className="text-slate-400 hover:text-rose-600"
                    aria-label={`Remove ${e.name}`}
                    data-testid={`invitee-chip-remove-${e.user_id}`}
                  >×</button>
                </span>
              ))}
            </div>
          )}

          {/* Search box */}
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={inviteeQuery}
              onChange={(e) => setInviteeQuery(e.target.value)}
              placeholder="Search by name, role or department…"
              className="pl-9"
              data-testid="invitee-search"
              autoComplete="off"
            />
          </div>

          <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-50" data-testid="invitee-list">
            {filteredEmployees.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="invitee-empty">
                No teammate matches “{inviteeQuery}”.
              </div>
            ) : filteredEmployees.map((e) => (
              <label key={e.user_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                <Checkbox checked={form.attendee_user_ids.includes(e.user_id)} onCheckedChange={() => toggle(e.user_id)} />
                <Avatar className="h-6 w-6"><AvatarImage src={e.avatar_url} /><AvatarFallback className="text-[10px]">{e.name.split(" ").map(p => p[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <div className="text-sm">
                  <div className="text-slate-900">{e.name}</div>
                  <div className="text-xs text-slate-500">{e.designation}</div>
                </div>
              </label>
            ))}
          </div>
          {selectedRoom && form.attendee_user_ids.length + 1 > selectedRoom.capacity && (
            <div className="mt-2 text-[11px] text-amber-700">
              {selectedRoom.name} seats {selectedRoom.capacity} — you've invited {form.attendee_user_ids.length + 1} people.
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit}
                disabled={!!conflict}
                className="bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                data-testid="meeting-submit">
          {isEdit ? "Save changes" : (needsApproval ? "Submit for approval" : "Schedule")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
